import { motion } from "framer-motion";
import {
  FileCheck,
  Camera,
  Shield,
  Clock,
  Wallet,
  Undo2,
} from "lucide-react";

/**
 * Guarantees — "Naše záruky pre vás".
 *
 * Explicit, contract-anchored promises that reduce client anxiety BEFORE
 * they click the CTA. Emmy's insight: nervous clients need a risk-reducer.
 *
 * Every guarantee here MUST be true and legally defensible — no marketing
 * fluff. If a promise cannot be signed into contract, remove it.
 */
const GUARANTEES = [
  {
    icon: FileCheck,
    title: "Fixná cena v zmluve",
    desc: "Cena, ktorú vám podpíšeme, sa nemení. Žiadne skryté príplatky ani prekvapenia počas stavby. Prípadné zmeny len po vašom písomnom súhlase.",
    accent: "cyan",
  },
  {
    icon: Camera,
    title: "Denné foto-reporty",
    desc: "Každý pracovný deň dostanete fotky a krátky video-report priamo na WhatsApp alebo e-mail. Vidíte postup aj z práce.",
    accent: "orange",
  },
  {
    icon: Shield,
    title: "3-ročná záruka písomne",
    desc: "36 mesiacov záruky na stavebné práce, 10 rokov na strechy a hydroizolácie. Reklamácie riešime do 48 h.",
    accent: "cyan",
  },
  {
    icon: Clock,
    title: "Zmluvné penále za omeškanie",
    desc: "Ak nedodržíme dohodnutý termín z našej strany, platíme vám zmluvné penále. Zapísané priamo v zmluve.",
    accent: "orange",
  },
  {
    icon: Wallet,
    title: "Platba podľa etáp",
    desc: "Neplatíte celú sumu vopred. Platby sú viazané na dokončené etapy — hrubá stavba, strecha, dokončovačky. Nikdy nezaplatíte za nespravenú prácu.",
    accent: "cyan",
  },
  {
    icon: Undo2,
    title: "7-dňová skúšobná doba",
    desc: "Po podpise zmluvy máte 7 dní na rozmyslenie. V tejto lehote môžete od zmluvy odstúpiť bez sankcie a zálohu vám vrátime v plnej výške.",
    accent: "orange",
  },
];

const ACCENTS = {
  cyan: { bg: "bg-[#00E5FF]/10", border: "border-[#00E5FF]/25", text: "text-[#00E5FF]" },
  orange: { bg: "bg-[#FF5E00]/10", border: "border-[#FF5E00]/25", text: "text-[#FF5E00]" },
};

export default function Guarantees() {
  return (
    <section
      id="zaruky"
      className="py-20 bg-black relative overflow-hidden"
      data-testid="guarantees-section"
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full opacity-[0.05] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #00E5FF, transparent)" }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="text-center mb-14">
          <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black mb-3">
            Naše záruky · Zapísané v zmluve · Nie sľuby, ale povinnosti
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Riziko <span className="text-zinc-500">berieme</span>
            <br />
            <span className="text-[#00E5FF] neon-cyan">my</span>
            <span className="text-[#FF5E00]">.</span>
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            Toto nie sú marketingové sľuby. Každý bod nižšie je zapísaný priamo v zmluve —
            aby ste presne vedeli, čo od nás dostanete.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GUARANTEES.map((g, i) => {
            const Icon = g.icon;
            const a = ACCENTS[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-white/20 transition-all group"
                data-testid={`guarantee-${i}`}
              >
                <div className={`w-12 h-12 rounded-xl ${a.bg} border ${a.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon size={22} className={a.text} />
                </div>
                <h3 className="text-lg font-black tracking-tight mb-2 text-white">
                  {g.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {g.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-10 p-5 rounded-2xl bg-gradient-to-r from-[#00E5FF]/10 to-transparent border border-[#00E5FF]/20 text-center sm:text-left flex flex-col sm:flex-row items-center gap-4 justify-between"
          data-testid="guarantees-cta"
        >
          <div className="text-sm sm:text-base text-zinc-200 max-w-xl">
            <span className="text-white font-bold">Nemáte istotu?</span> Vyžiadajte si vzor zmluvy vopred —
            uvidíte presne, čo budete podpisovať, ešte pred obhliadkou.
          </div>
          <a
            href="#kontakt"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#00E5FF] hover:bg-[#33EEFF] text-black font-bold rounded-xl text-sm whitespace-nowrap transition-colors"
            data-testid="guarantees-cta-btn"
          >
            Poslať vzor zmluvy
          </a>
        </motion.div>
      </div>
    </section>
  );
}
